import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nProvider } from '../i18n';
import { AnalyticsProvider, createAppSink, noopSink, type AnalyticsSink } from '../lib/analytics';
import { FlagsProvider, type Flags, type GrowthBookOptions } from '../lib/flags';
import { ApiError, ApiProvider, NetworkError, apiFromEnv, useApi, type IntakeApi } from '../lib/api';
import {
  expiredLink,
  landReferral,
  readAttribution,
  setNavContext,
  tripDraft,
  updateTripDraft,
} from '../lib/funnel';
import { captureException, isReportable } from '../lib/errors';
import { ToastProvider } from '../chrome/Toast';
import { BASENAME, routes } from './routes';
import { pathFor, stepHref } from './navigation';

/**
 * Reads are retried when the answer never came or the API stumbled (the network, a timeout,
 * a 5xx): twice, 1 s then 2 s apart. A 429 waits its Retry-After once (up to 10 s). Any other
 * 4xx is an answer, and a response that doesn't parse is a bug: neither is retried.
 */
export function retryRead(count: number, error: unknown): boolean {
  if (error instanceof NetworkError) return count < 2;
  if (error instanceof ApiError) {
    if (error.isRateLimited) return count < 1 && (error.retryAfter ?? 1) <= 10;
    return error.status >= 500 && count < 2;
  }
  return false;
}

export function retryReadDelay(count: number, error: unknown): number {
  if (error instanceof ApiError && error.isRateLimited && error.retryAfter !== null)
    return error.retryAfter * 1000;
  return Math.min(1000 * 2 ** count, 8000);
}

export function createQueryClient() {
  return new QueryClient({
    // Screens show their own failure states; a 5xx or a broken response still goes to Sentry
    // (not a 4xx answer or the visitor's network: isReportable).
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isReportable(error))
          captureException(error, { kind: 'query', op: String(query.queryKey[2] ?? query.queryKey[0]) });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        if (isReportable(error)) captureException(error, { kind: 'mutation' });
      },
    }),
    defaultOptions: {
      queries: { retry: retryRead, retryDelay: retryReadDelay, refetchOnWindowFocus: false },
    },
  });
}

export interface ProvidersProps {
  api?: IntakeApi;
  analytics?: AnalyticsSink;
  flags?: Partial<Flags>;
  /** GrowthBook (the app: from the env; tests: none, local defaults). */
  growthbook?: GrowthBookOptions | null;
  queryClient?: QueryClient;
  children: ReactNode;
}

/** Everything a screen can reach through hooks. Tests render screens inside this too. */
export function Providers({
  api,
  analytics = noopSink,
  flags,
  growthbook = null,
  queryClient,
  children,
}: ProvidersProps) {
  const [qc] = useState(() => queryClient ?? createQueryClient());
  const [client] = useState(() => api ?? apiFromEnv());
  return (
    <I18nProvider>
      <FlagsProvider overrides={flags} growthbook={growthbook}>
        <AnalyticsProvider sink={analytics}>
          <QueryClientProvider client={qc}>
            <ApiProvider api={client}>
              <ToastProvider>{children}</ToastProvider>
            </ApiProvider>
          </QueryClientProvider>
        </AnalyticsProvider>
      </FlagsProvider>
    </I18nProvider>
  );
}

type AppRouter = ReturnType<typeof createBrowserRouter>;

/**
 * The landing URL, handled once before any screen mounts:
 * - **`?t=` (a magic link):** removed from the address bar at once (`replace`), then
 *   `POST /auth/exchange`. Success → the resolver's `next` for that link, with this tab's
 *   context set to `returning` + the link's target (and the passenger, for their own link).
 *   `410` → S14-expired, with the details in `expiredLink` for the screen. Anything else
 *   (unknown link, offline) → the page the link pointed at, whose own calls then decide.
 * - **utm_* / AffiliateWP cookies:** kept in the trip draft, sent with `createEnquiry`.
 * - **`?buddy=` (a Buddy link):** resolved in the background (`landReferral`: AffiliateWP's
 *   cookies set, the draft's `affwpRef` made the affiliate id). Only creating the enquiry
 *   waits for it, briefly.
 */
export function EntryGate({ router, children }: { router: AppRouter; children: ReactNode }) {
  const api = useApi();
  const [token] = useState(() => new URLSearchParams(router.state.location.search).get('t'));
  const [pending, setPending] = useState(() => !!token);
  const started = useRef(false);

  useLayoutEffect(() => {
    if (started.current) return;
    started.current = true;
    // router.state.location includes the basename; router.navigate() adds it back.
    const { pathname: full, search } = router.state.location;
    const pathname = full.startsWith(BASENAME) ? full.slice(BASENAME.length) || '/' : full;
    const found = readAttribution(search, full);
    if (Object.keys(found).length)
      updateTripDraft({ attribution: { ...tripDraft.get().attribution, ...found } });
    landReferral(api, search, window.location.href, document.referrer);

    const t = token;
    if (!t) return;
    const rest = new URLSearchParams(search);
    rest.delete('t');
    void router.navigate({ pathname, search: rest.size ? `?${rest}` : '' }, { replace: true });
    api
      .exchangeLink(t)
      .then(async (res) => {
        expiredLink.clear();
        setNavContext({
          visit: 'returning',
          target: res.target,
          passenger: res.scope.role === 'passenger' ? res.scope.passenger : null,
        });
        await router.navigate(stepHref(res.next, res.scope.enquiryId), { replace: true });
      })
      .catch(async (e: unknown) => {
        if (e instanceof ApiError && e.isExpired && e.expiredLink) {
          expiredLink.set({ token: t, details: e.expiredLink });
          await router.navigate(pathFor('S14-expired', { id: e.expiredLink.enquiryId }), { replace: true });
        }
      })
      .finally(() => setPending(false));
  }, [api, router, token]);

  return pending ? null : <>{children}</>;
}

/**
 * The app's API client and analytics, made once per page (StrictMode runs state initialisers
 * twice; a second sink would install its page listeners and consent loaders twice).
 */
let services: { api: IntakeApi; analytics: AnalyticsSink; growthbook: GrowthBookOptions | null } | null =
  null;
function appServices() {
  if (!services) {
    const api = apiFromEnv();
    const analytics = createAppSink(api);
    const clientKey = import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY;
    const growthbook = clientKey
      ? {
          clientKey,
          apiHost: import.meta.env.VITE_GROWTHBOOK_API_HOST || undefined,
          onExperiment: (experimentId: string, variationId: string) =>
            analytics.track({ name: 'experiment_viewed', experimentId, variationId }),
        }
      : null;
    services = { api, analytics, growthbook };
  }
  return services;
}

export function App() {
  const [router] = useState(() => createBrowserRouter(routes, { basename: BASENAME }));
  const [{ api, analytics, growthbook }] = useState(appServices);
  return (
    <Providers api={api} analytics={analytics} growthbook={growthbook}>
      <EntryGate router={router}>
        <RouterProvider router={router} />
      </EntryGate>
    </Providers>
  );
}
